import {useState} from 'react';
import {Check, Clock, Copy, Eye, Loader2} from 'lucide-react';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';

// The dark chrome bar above the flow in preview mode: a PREVIEW pill + site name, the
// saved-draft freshness / live-reload state, and a Share action. Null outside preview.
export function PreviewBanner() {
  const {isPreview, isDemo, savedAt, refreshing} = useEnrollPreview();
  const group = useEnrollGroup();
  const [copied, setCopied] = useState(false);

  if (!isPreview && !isDemo) return null;

  function share() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard blocked (denied / document not focused) — leave the button idle.
      });
  }

  return (
    <div className='sticky top-0 z-[60] border-b border-white/10 bg-slate-900 py-2 text-xs text-white'>
      <div className='mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-6'>
        <div className='flex min-w-0 items-center gap-2'>
          <span className='inline-flex flex-none items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide'>
            <Eye className='h-3 w-3' />
            <span className='hidden sm:inline'>{isDemo ? 'Demo' : 'Preview'}</span>
          </span>
          <span className='truncate font-medium'>{group.name}</span>
        </div>

        {isDemo ? (
          <div className='flex items-center gap-1.5 font-medium text-slate-200'>
            <span>Enrollments aren’t submitted</span>
          </div>
        ) : (
          <>
            <div className='flex items-center gap-1.5 font-medium text-slate-200'>
              {refreshing ? (
                <>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  <span>Updating…</span>
                </>
              ) : (
                <>
                  <Clock className='h-3.5 w-3.5 text-slate-300' />
                  <span>
                    Saved draft
                    {savedAt && <span className='font-normal text-slate-300'> · last saved {savedAt}</span>}
                  </span>
                </>
              )}
            </div>

            <button
              type='button'
              onClick={share}
              title='Copy a link — anyone at CBS with enrollment access can open it'
              className='inline-flex flex-none items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1 font-medium transition-colors hover:bg-white/20'
            >
              {copied ? <Check className='h-3.5 w-3.5 text-emerald-400' /> : <Copy className='h-3.5 w-3.5' />}
              <span className='hidden sm:inline'>{copied ? 'Link copied' : 'Share'}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
